import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";

import Table from "examples/Tables/Table";
import { useAuth } from "context/AuthContext";
import {
  guestAirports,
  guestNonprofits,
} from "data/guestDemoData";
import { getEntities } from "./Users/services/graphqlService";
import { subscribeToGlobal } from "./Users/services/subscriptionService";
import ViewAirportModal from "./Users/components/ViewAirportModal";
import ViewNonprofitModal from "./Users/components/ViewNonprofitModal";
import { canManageEntities } from "./missionOwnerUtils";

function pickAddressParts(entity) {
  const address = entity?.address;
  let street = "";
  let city = entity?.city || "";
  let state = entity?.state || "";

  const applyItem = (item) => {
    if (!item) return;
    if (typeof item === "string") {
      street = street || item;
      return;
    }
    if (typeof item === "object") {
      street = street || item.address || "";
      city = city || item.city || "";
      state = state || item.state || "";
    }
  };

  if (Array.isArray(address)) {
    applyItem(address[0]);
  } else {
    applyItem(address);
  }

  return { street, city, state };
}

function EntitiesPage() {
  const navigate = useNavigate();
  const { user, isGuest, tokenEntityType } = useAuth();
  const canEditEntities = canManageEntities({ tokenEntityType, isGuest });
  const [activeTab, setActiveTab] = useState(0);
  const [nonprofits, setNonprofits] = useState([]);
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [viewAirportModalOpen, setViewAirportModalOpen] = useState(false);
  const [viewAirportId, setViewAirportId] = useState(null);
  const [viewNonprofitModalOpen, setViewNonprofitModalOpen] = useState(false);
  const [viewNonprofitId, setViewNonprofitId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!user?.id_token) {
      setError("Authentication required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const all = [];
      let nextToken = null;
      let pages = 0;
      do {
        const page = await getEntities(user.id_token, null, nextToken);
        all.push(...(Array.isArray(page.data) ? page.data : []));
        nextToken = page.nextToken;
        pages += 1;
      } while (nextToken && pages < 50);

      const typeKey = (t) =>
        String(t || "")
          .toLowerCase()
          .replace(/[-_\s]/g, "");
      setNonprofits(
        all.filter((e) => {
          const k = typeKey(e.type);
          return k === "nonprofit" || k === "nonprofits";
        })
      );
      setAirports(all.filter((e) => typeKey(e.type) === "airport"));
    } catch (err) {
      console.error("Error fetching entities:", err);
      setError(err.message || "Failed to load entities");
      // Set empty arrays on error
      setNonprofits([]);
      setAirports([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isGuest) {
      setNonprofits(guestNonprofits);
      setAirports(guestAirports);
      setLoading(false);
      setError(null);
    }
  }, [isGuest]);

  // Fetch data on component mount
  useEffect(() => {
    if (isGuest) return;
    if (user?.id_token) {
      fetchData();

      // Set up subscription for real-time updates
      let unsubscribe = null;
      const eventId = "entities-update"; // You can customize this

      subscribeToGlobal(
        eventId,
        (message) => {
          // When a global message is received, refresh the data
          if (
            message.Type === "entity-update" ||
            message.Type === "Airport" ||
            message.Type === "airport" ||
            message.Type === "NonProfit" ||
            message.Type === "nonprofit"
          ) {
            fetchData();
          }
        },
        (err) => {
          console.error("Subscription error:", err);
        }
      )
        .then((unsubFn) => {
          unsubscribe = unsubFn;
        })
        .catch((err) => {
          console.error("Failed to set up subscription:", err);
        });

      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [isGuest, user, fetchData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setStateFilter("");
    setCategoryFilter("");
  };

  const handleAddEntity = () => {
    navigate("/dashboard/add-entity");
  };

  const handleRefresh = () => {
    fetchData();
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      medical: "error",
      "animal-rescue": "warning",
      "disaster-relief": "info",
      "veteran-support": "success",
      conservation: "success",
      "aviation-experience": "primary",
      other: "secondary",
      public: "success",
      private: "info",
      "open-to-support-missions": "warning",
    };
    return colors[category] || "secondary";
  };

  const getLocationString = (entity) => {
    const { street, city, state } = pickAddressParts(entity);
    return [street, city, state].filter(Boolean).join(" ");
  };

  // Distinct filter option lists, derived from whichever list is active.
  const nonprofitStates = Array.from(
    new Set(
      nonprofits
        .map((np) => pickAddressParts(np).state)
        .filter((state) => state && state.trim()),
    ),
  ).sort();
  const nonprofitCategories = Array.from(
    new Set(
      nonprofits.map((np) => np.category).filter((c) => c && c.trim()),
    ),
  ).sort();
  const airportStates = Array.from(
    new Set(
      airports
        .map((a) => pickAddressParts(a).state)
        .filter((state) => state && state.trim()),
    ),
  ).sort();
  const airportCategories = Array.from(
    new Set(
      airports.map((a) => a.airportUse).filter((c) => c && c.trim()),
    ),
  ).sort();

  const matchesFilters = (entity, { state, category }) => {
    if (state && pickAddressParts(entity).state !== state) return false;
    if (category && entity.category !== category && entity.airportUse !== category) {
      return false;
    }
    return true;
  };

  const filteredNonprofits = nonprofits
    .filter((np) => matchesFilters(np, { state: stateFilter, category: categoryFilter }))
    .filter((np) => {
      if (!searchQuery.trim()) return true;
        const q = searchQuery.trim().toLowerCase();
        const name = (np.name || "").toLowerCase();
        const email = (np.email || "").toLowerCase();
        const ein = (np.ein || "").toLowerCase();
        const category = (np.category || "").toLowerCase();
        const location = getLocationString(np).toLowerCase();
        return (
          name.includes(q) ||
          email.includes(q) ||
          ein.includes(q) ||
          category.includes(q) ||
          location.includes(q)
        );
    });

  const filteredAirports = airports
    .filter((a) => matchesFilters(a, { state: stateFilter, category: categoryFilter }))
    .filter((airport) => {
      if (!searchQuery.trim()) return true;
        const q = searchQuery.trim().toLowerCase();
        const name = (airport.name || "").toLowerCase();
        const email = (airport.email || "").toLowerCase();
        const phone = (airport.phone || "").toLowerCase();
        const managerName = (airport.managerName || "").toLowerCase();
        const airportUse = (airport.airportUse || "").toLowerCase();
        const airportOwnership = (airport.airportOwnership || "").toLowerCase();
        const category = (airport.category || "").toLowerCase();
        const location = getLocationString(airport).toLowerCase();
        return (
          name.includes(q) ||
          email.includes(q) ||
          phone.includes(q) ||
          managerName.includes(q) ||
          airportUse.includes(q) ||
          airportOwnership.includes(q) ||
          category.includes(q) ||
          location.includes(q)
        );
    });

  // Non-Profit Table
  const nonprofitColumns = [
    { name: "name", align: "left" },
    { name: "ein", align: "left" },
    { name: "category", align: "center" },
    { name: "location", align: "left" },
    { name: "actions", align: "center" },
  ];

  const nonprofitRows = filteredNonprofits.map((np, index) => ({
    name: (
      <SoftBox display="flex" flexDirection="column">
        <SoftTypography variant="button" fontWeight="medium">
          {np.name}
        </SoftTypography>
        <SoftTypography variant="caption" color="secondary">
          {np.email}
        </SoftTypography>
      </SoftBox>
    ),
    ein: (
      <SoftTypography variant="caption" color="secondary">
        {np.ein}
      </SoftTypography>
    ),
    category: (
      <SoftBadge
        variant="contained"
        badgeContent={(np.category || "other").replace(/-/g, " ")}
        color={getCategoryBadgeColor(np.category || "other")}
        size="xs"
        container
      />
    ),
    location: (() => {
      // Handle multiple addresses for nonprofits - show only City, State
      let locationParts = [];

      // Check if address array exists
      if (np.address && Array.isArray(np.address) && np.address.length > 0) {
        // Get first address city, state
        const firstAddr = np.address[0];
        if (firstAddr.city && firstAddr.state) {
          locationParts.push(`${firstAddr.city}, ${firstAddr.state}`);
        } else if (firstAddr.city) {
          locationParts.push(firstAddr.city);
        } else if (firstAddr.state) {
          locationParts.push(firstAddr.state);
        }

        // If there are more than 1 address, indicate there are more
        if (np.address.length > 1) {
          locationParts.push(`(+${np.address.length - 1} more)`);
        }
      } else {
        // Fallback to top-level city/state
        if (np.city && np.state) {
          locationParts.push(`${np.city}, ${np.state}`);
        } else if (np.city) {
          locationParts.push(np.city);
        } else if (np.state) {
          locationParts.push(np.state);
        }
      }

      if (locationParts.length === 0) {
        return (
          <SoftTypography variant="caption" color="secondary">
            N/A
          </SoftTypography>
        );
      }

      return (
        <SoftTypography variant="caption" color="secondary">
          {locationParts.join(" ")}
        </SoftTypography>
      );
    })(),
    actions: (
      <SoftBox display="flex" gap={1}>
        <SoftButton
          variant="text"
          color="info"
          size="small"
          onClick={() => {
            setViewNonprofitId(np.nonprofitId || np.entityId || np.id);
            setViewNonprofitModalOpen(true);
          }}
        >
          <Icon>visibility</Icon>&nbsp;View
        </SoftButton>
        {canEditEntities && (
          <SoftButton
            variant="text"
            color="dark"
            size="small"
            onClick={() =>
              navigate(
                `/dashboard/add-entity?entityType=NonProfit&id=${
                  np.nonprofitId || np.entityId || np.id
                }`
              )
            }
          >
            <Icon>edit</Icon>&nbsp;Edit
          </SoftButton>
        )}
      </SoftBox>
    ),
  }));

  // Airport Table
  const airportColumns = [
    { name: "name", align: "left" },
    { name: "location", align: "left" },
    { name: "manager", align: "left" },
    { name: "use", align: "center" },
    { name: "ownership", align: "center" },
    { name: "contact", align: "left" },
    { name: "actions", align: "center" },
  ];

  const airportRows = filteredAirports.map((airport) => ({
    name: (
      <SoftBox display="flex" flexDirection="column">
        <SoftTypography variant="button" fontWeight="medium">
          {airport.name}
        </SoftTypography>
        {airport.email && (
          <SoftTypography variant="caption" color="secondary">
            {airport.email}
          </SoftTypography>
        )}
      </SoftBox>
    ),
    location: (() => {
      const { street, city, state } = pickAddressParts(airport);
      const locationParts = [];

      if (street) {
        locationParts.push(
          <SoftTypography key="address" variant="caption" color="text">
            {street}
          </SoftTypography>
        );
      }

      if (city && state) {
        locationParts.push(
          <SoftTypography key="citystate" variant="caption" color="secondary">
            {city}, {state}
          </SoftTypography>
        );
      } else if (city) {
        locationParts.push(
          <SoftTypography key="city" variant="caption" color="secondary">
            {city}
          </SoftTypography>
        );
      } else if (state) {
        locationParts.push(
          <SoftTypography key="state" variant="caption" color="secondary">
            {state}
          </SoftTypography>
        );
      }

      if (locationParts.length === 0) {
        return (
          <SoftTypography variant="caption" color="secondary">
            N/A
          </SoftTypography>
        );
      }

      return (
        <SoftBox display="flex" flexDirection="column" gap={0.5}>
          {locationParts}
        </SoftBox>
      );
    })(),
    manager: (
      <SoftBox display="flex" flexDirection="column">
        {airport.managerName && (
          <SoftTypography variant="caption" color="text">
            {airport.managerName}
          </SoftTypography>
        )}
        {airport.managerPhone && (
          <SoftTypography variant="caption" color="secondary">
            {airport.managerPhone}
          </SoftTypography>
        )}
      </SoftBox>
    ),
    use: (
      <SoftBadge
        variant="contained"
        badgeContent={airport.airportUse || airport.category || "N/A"}
        color={getCategoryBadgeColor(
          airport.airportUse || airport.category
        )}
        size="xs"
        container
      />
    ),
    ownership: (
      <SoftBadge
        variant="contained"
        badgeContent={airport.airportOwnership || "N/A"}
        color={getCategoryBadgeColor(airport.airportOwnership)}
        size="xs"
        container
      />
    ),
    contact: (
      <SoftBox display="flex" flexDirection="column">
        {airport.phone && (
          <SoftTypography variant="caption" color="text">
            {airport.phone}
          </SoftTypography>
        )}
        {airport.url && (
          <SoftTypography
            variant="caption"
            color="info"
            component="a"
            href={airport.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Website
          </SoftTypography>
        )}
      </SoftBox>
    ),
    actions: (
      <SoftBox display="flex" gap={1}>
        <SoftButton
          variant="text"
          color="info"
          size="small"
          onClick={() => {
            setViewAirportId(airport.airportId || airport.entityId || airport.id);
            setViewAirportModalOpen(true);
          }}
        >
          <Icon>visibility</Icon>&nbsp;View
        </SoftButton>
        {canEditEntities && (
          <SoftButton
            variant="text"
            color="dark"
            size="small"
            onClick={() =>
              navigate(
                `/dashboard/add-entity?entityType=Airport&id=${
                  airport.airportId || airport.entityId || airport.id
                }`
              )
            }
          >
            <Icon>edit</Icon>&nbsp;Edit
          </SoftButton>
        )}
      </SoftBox>
    ),
  }));

  return (
    <SoftBox py={3}>
      <SoftBox mb={3}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <SoftTypography variant="h4" fontWeight="bold">
              Entity Management
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              {isGuest
                ? "Sample non-profits and airports (read-only)."
                : canEditEntities
                  ? "Manage non-profit organizations and airports"
                  : "View non-profit organizations and airports"}
            </SoftTypography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            justifyContent="flex-end"
            gap={1}
          >
            {/* <SoftButton
              variant="outlined"
              color="secondary"
              size="large"
              onClick={handleRefresh}
              disabled={loading}
            >
              <Icon sx={{ mr: 1 }}>refresh</Icon>
              Refresh
            </SoftButton> */}
            {canEditEntities && (
              <SoftButton
                variant="gradient"
                color="dark"
                size="large"
                onClick={handleAddEntity}
              >
                <Icon sx={{ mr: 1 }}>add</Icon>
                Add Entity
              </SoftButton>
            )}
          </Grid>
        </Grid>
      </SoftBox>

      {error && (
        <SoftBox mb={3}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </SoftBox>
      )}

      {loading && (
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={8}
        >
          <CircularProgress />
        </SoftBox>
      )}

      {!loading && (
        <Card>
          <SoftBox>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{ px: 2, pt: 2 }}
            >
              <Tab label={`Non-Profits (${nonprofits.length})`} />
              <Tab label={`Airports (${airports.length})`} />
            </Tabs>

            <SoftBox p={2}>
              {/* Filters + Search Bar */}
              {!loading &&
                ((activeTab === 0 && nonprofits.length > 0) ||
                  (activeTab === 1 && airports.length > 0)) && (
                  <SoftBox
                    mb={2}
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="flex-end"
                    gap={1.5}
                    sx={{ maxWidth: "100%" }}
                  >
                    <SoftBox sx={{ minWidth: 170 }}>
                      <SoftSelect
                        placeholder="Operational state"
                        value={
                          stateFilter
                            ? { value: stateFilter, label: stateFilter }
                            : null
                        }
                        onChange={(opt) => setStateFilter(opt?.value || "")}
                        isClearable
                        options={(activeTab === 0
                          ? nonprofitStates
                          : airportStates
                        ).map((state) => ({ value: state, label: state }))}
                      />
                    </SoftBox>
                    <SoftBox sx={{ minWidth: 170 }}>
                      <SoftSelect
                        placeholder={activeTab === 0 ? "Category" : "Airport use"}
                        value={
                          categoryFilter
                            ? { value: categoryFilter, label: categoryFilter }
                            : null
                        }
                        onChange={(opt) => setCategoryFilter(opt?.value || "")}
                        isClearable
                        options={(activeTab === 0
                          ? nonprofitCategories
                          : airportCategories
                        ).map((c) => ({ value: c, label: c }))}
                      />
                    </SoftBox>
                    <SoftBox
                      sx={{
                        width: 360,
                        maxWidth: "100%",
                        "&:focus-within": {
                          border: "2px solid",
                          borderColor: "info.main",
                        },
                        "&:focus-within .MuiInputBase-root.Mui-focused": {
                          boxShadow: "none",
                          borderColor: "transparent !important",
                        },
                      }}
                    >
                      <SoftInput
                        placeholder={
                          activeTab === 0
                            ? "Search nonprofits by name, email, EIN, category, or location..."
                            : "Search airports by name, email, phone, manager, use, ownership, or location..."
                        }
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        icon={{ component: "search", direction: "left" }}
                      />
                    </SoftBox>
                  </SoftBox>
                )}

              {activeTab === 0 &&
                (nonprofits.length > 0 ? (
                  filteredNonprofits.length > 0 ? (
                    <Table columns={nonprofitColumns} rows={nonprofitRows} />
                  ) : (
                    <SoftBox py={4} textAlign="center">
                      <SoftTypography variant="body2" color="text">
                        No non-profits match &quot;{searchQuery.trim()}&quot;.
                        Try a different search.
                      </SoftTypography>
                    </SoftBox>
                  )
                ) : (
                  <SoftBox py={4} textAlign="center">
                    <SoftTypography variant="body2" color="text">
                      {canEditEntities
                        ? 'No non-profits found. Click "Add Entity" to create one.'
                        : "No non-profits found."}
                    </SoftTypography>
                  </SoftBox>
                ))}
              {activeTab === 1 &&
                (airports.length > 0 ? (
                  filteredAirports.length > 0 ? (
                    <Table columns={airportColumns} rows={airportRows} />
                  ) : (
                    <SoftBox py={4} textAlign="center">
                      <SoftTypography variant="body2" color="text">
                        No airports match &quot;{searchQuery.trim()}&quot;. Try
                        a different search.
                      </SoftTypography>
                    </SoftBox>
                  )
                ) : (
                  <SoftBox py={4} textAlign="center">
                    <SoftTypography variant="body2" color="text">
                      {canEditEntities
                        ? 'No airports found. Click "Add Entity" to create one.'
                        : "No airports found."}
                    </SoftTypography>
                  </SoftBox>
                ))}
            </SoftBox>
          </SoftBox>
        </Card>
      )}

      <ViewAirportModal
        open={viewAirportModalOpen}
        onClose={() => {
          setViewAirportModalOpen(false);
          setViewAirportId(null);
        }}
        airportId={viewAirportId}
        idToken={user?.id_token}
        prefetchedRecord={
          isGuest
            ? airports.find(
                (a) =>
                  String(a.airportId || a.entityId || a.id) ===
                  String(viewAirportId),
              )
            : undefined
        }
      />

      <ViewNonprofitModal
        open={viewNonprofitModalOpen}
        onClose={() => {
          setViewNonprofitModalOpen(false);
          setViewNonprofitId(null);
        }}
        nonprofitId={viewNonprofitId}
        idToken={user?.id_token}
        prefetchedRecord={
          isGuest
            ? nonprofits.find(
                (np) =>
                  String(np.nonprofitId || np.entityId || np.id) ===
                  String(viewNonprofitId),
              )
            : undefined
        }
      />
    </SoftBox>
  );
}

export default EntitiesPage;
